// askSmart-ui/src/pages/TOSP/components/RevenueBudgetPanel.tsx
import React, { useMemo, useState } from "react";
import {
  CAM_RECOVERY_KEY,
  MONTH_LABELS,
  projectRevenue,
  withCamRecovery,
} from "../revenueBudget";
import type { BudgetRevenueLine, BudgetRevenueSnapshot } from "../types";
import { useStoredSales } from "../portal/tenantSales";
import { statementRatesFor } from "../portal/tenantExpenses";
import { useTospData } from "../TospDataContext";

/** Month cells are whole dollars — thirteen columns of cents is unreadable. */
const money0 = (value: number): string =>
  value.toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 0 });

/** A rate, quoted the way a rate is rather than the way a total is. */
const psf = (value: number): string => `$${value.toFixed(2)}`;

const money2 = (value: number): string =>
  value.toLocaleString(undefined, { style: "currency", currency: "USD", minimumFractionDigits: 2 });

/**
 * A month cell. Nothing is written as $0.00 rather than a dash: on a budget,
 * zero is a figure — the month earns nothing — while a dash reads as a month
 * nobody has got to yet. The two are worth telling apart on a sheet that is
 * being filled in.
 */
const cellText = (value: number): string => (value === 0 ? money2(0) : money0(value));

// A label column, twelve months, then the year.
const GRID_COLUMNS = "1.7fr repeat(12, minmax(78px, 1fr)) minmax(104px, 1.2fr)";
const MIN_WIDTH = 1420;

interface RevenueBudgetPanelProps {
  year: number;
  /** Manual adjustments, keyed "<line key>:<month 0-11>". */
  overrides: Record<string, number>;
  /** `null` clears an adjustment and returns the cell to the projection. */
  onOverride: (key: string, amount: number | null) => void;
  /**
   * The figures as frozen when the budget was reviewed, shown INSTEAD of the
   * live projection — that is the whole point of freezing them.
   *
   * Passed only WHILE the budget is reviewed. A budget whose Reviewed step has
   * been un-ticked still carries its snapshot as a record, but is being worked
   * on again and so reads live, like any other unreviewed budget.
   */
  snapshot?: BudgetRevenueSnapshot;
  /**
   * The CAM recovery worked out by the Calculate CAMS step, slotted in under
   * 4100 beside the other recoveries. Absent until that step has been run —
   * and already inside `snapshot` on a reviewed budget, so it is merged into
   * the live projection only.
   */
  camRecovery?: BudgetRevenueLine;
  /** A published budget is read-only: its figures are a record, not a draft. */
  locked?: boolean;
  /**
   * unitId → tenantId, the occupancy the BUDGET states rather than derives —
   * `budget.camOccupancy`.
   *
   * THE PANEL PROJECTS ITS OWN FIGURES, so it needs this too. Handing it only
   * to the publish and statement paths left the sheet on screen disagreeing
   * with everything built from it: Greekery was stated as holding BH4 182 #2
   * for 2025, appeared on the statements and in the snapshot, and was absent
   * from the very screen the admin reads.
   */
  assume?: Record<string, string>;
}

/** The key a LINE adjustment is stored under. */
const overrideKey = (line: string, month: number): string => `${line}:${month}`;

/**
 * The key a TENANT adjustment is stored under.
 *
 * Distinct from the line's own key by having three parts. A tenant nobody could
 * project — a new business, or one that took a space over mid-year — is typed
 * against the tenant it belongs to, and the line total follows from the rows
 * rather than having to be adjusted separately and kept in step by hand.
 */
const tenantKey = (line: string, tenantId: string, month: number): string =>
  `${line}:${tenantId}:${month}`;

// Step 1 of the Budget wizard. Everything here is projected from lease, tax
// roll and unit data already in TOSP; nothing is typed in. The admin reads it,
// keys the figures into QuickBooks, then imports the completed budget back to
// bring in the expense side — the import button for which lives on the budget
// screen's toolbar, above this panel.
export default function RevenueBudgetPanel({
  year,
  overrides,
  onOverride,
  snapshot,
  camRecovery,
  locked = false,
  assume,
}: RevenueBudgetPanelProps) {
  const { units, tenants, unitById, unitLabel, configuration, taxRolls, budgets } =
    useTospData();
  // Percentage rent is projected from what tenants have actually reported.
  const { sales } = useStoredSales();
  const [expanded, setExpanded] = useState<string[]>([]);
  /** Lines whose warning is open, by account. */
  const [warnings, setWarnings] = useState<string[]>([]);
  /** The cell being typed in, so keystrokes aren't reformatted mid-edit. */
  const [editing, setEditing] = useState<{ key: string; text: string } | null>(null);

  /**
   * Last year's CAM per square foot, per tenancy, from the year's PUBLISHED
   * budget — or null when there isn't one.
   *
   * THE COMPARISON IS AGAINST WHAT WAS AGREED, not against another draft. A
   * 2026 budget is read beside the 2025 budget that was published and billed
   * on; a second revision of 2026 is not something to compare 2026 with. So
   * this looks only at a budget whose Published step is done, and reads the
   * figure out of its frozen snapshot rather than recomputing last year from
   * today's leases — which would move every time a lease was corrected.
   *
   * `perSqFt.cams` is the published statement's own rate, so the figure here
   * and the figure the tenant was shown are the same number.
   */
  const previousCamPsf = useMemo(() => {
    const published = budgets.find(
      (candidate) =>
        candidate.year === year - 1 &&
        !!candidate.history?.published &&
        !!candidate.publishedSnapshot,
    );
    if (!published?.publishedSnapshot) return null;
    const byTenant = new Map<string, number>();
    published.publishedSnapshot.statements.forEach((statement) => {
      const rate = statement.perSqFt?.cams;
      if (rate !== null && rate !== undefined && rate > 0) {
        byTenant.set(statement.tenantId, rate);
      }
    });
    return byTenant.size > 0 ? byTenant : null;
  }, [budgets, year]);

  /** Area per tenancy, for turning this year's recovery into a rate. */
  const sqFtByTenant = useMemo(() => {
    const byTenant = new Map<string, number>();
    if (!previousCamPsf) return byTenant;
    tenants.forEach((tenant) => {
      const { sqFt } = statementRatesFor(tenant, unitById, taxRolls, year);
      if (sqFt) byTenant.set(tenant.id, sqFt);
    });
    return byTenant;
  }, [previousCamPsf, tenants, unitById, taxRolls, year]);

  /**
   * One tenancy's CAM rate against last year's, or null where there is
   * nothing honest to compare.
   *
   * Compared PER SQUARE FOOT so a tenancy that took on more space doesn't
   * read as a rise in what it is being charged. Null where last year's budget
   * was never published, where the tenancy wasn't on it — a new arrival has
   * no history to compare with — or where its area isn't recorded.
   */
  const camChange = (
    tenantId: string,
    total: number,
  ): { previous: number; current: number; percent: number; up: boolean } | null => {
    const previous = previousCamPsf?.get(tenantId);
    const sqFt = sqFtByTenant.get(tenantId);
    if (!previous || !sqFt) return null;
    const current = total / sqFt;
    const percent = ((current - previous) / previous) * 100;
    return { previous, current, percent, up: percent > 0 };
  };

  const projection = useMemo(
    () =>
      projectRevenue(year, units, tenants, unitLabel, configuration, taxRolls, sales, assume),
    [year, units, tenants, unitLabel, configuration, taxRolls, sales, assume],
  );

  // A reviewed budget reads its own frozen figures; everything else is
  // projected live, so a lease or a rate corrected while the budget is being
  // worked up moves it. RevenueLine satisfies the stored shape, so both paths
  // render through the same code below.
  const projectedLines: BudgetRevenueLine[] = projection.lines;
  // The recovery is merged into whichever set is being read. A snapshot taken
  // after the CAM step carries its own; one taken before needs this, or the
  // 41001 never appears on a budget that has ever been reviewed.
  const lines: BudgetRevenueLine[] = withCamRecovery(
    snapshot ? snapshot.lines : projectedLines,
    camRecovery,
  );

  /** What a month is worth once any manual adjustment is taken into account. */
  const amountOf = (key: string, projected: number): number => {
    const override = overrides[key];
    return override === undefined ? projected : override;
  };

  const isEdited = (key: string): boolean => overrides[key] !== undefined;

  /** One tenant's month on a line, their own adjustment included. */
  const tenantAmount = (
    lineKey: string,
    row: { id: string; months: number[] },
    month: number,
  ): number => amountOf(tenantKey(lineKey, row.id, month), row.months[month]);

  /**
   * A line's twelve months.
   *
   * Built from the tenant rows beneath it, because that is what every line
   * already is — each one's months are the sum of its contributions. So a
   * figure typed against one tenant moves the line without anything having to
   * keep the two in step. A line-level adjustment still wins where one is set:
   * an expected total is sometimes known when its division is not.
   */
  const monthsOf = (line: BudgetRevenueLine): number[] =>
    line.months.map((projected, m) => {
      const own = overrides[overrideKey(line.key, m)];
      if (own !== undefined) return own;
      if (line.contributions.length === 0) return projected;
      return line.contributions.reduce(
        (total, row) => total + tenantAmount(line.key, row, m),
        0,
      );
    });

  // Typed text becomes a figure on leaving the cell, not on every keystroke:
  // "1,2" is a number on its way to being typed, not an adjustment of $1.20.
  //
  // `current` is what the cell held when it was focused, `projected` what the
  // leases produce. Clicking into a cell and out again must change NOTHING —
  // it briefly did, because the cell was seeded with the figure ROUNDED for
  // display, so leaving $23,563.52 wrote back 23564 and that counted as an
  // adjustment.
  const commit = (key: string, text: string, projected: number, current: number) => {
    setEditing(null);
    const trimmed = text.trim().replace(/[$,\s]/g, "");
    if (!trimmed) {
      // Empty means "put the projection back" — but only where an adjustment
      // exists to undo. Most of this grid is blank, and clicking through blank
      // cells was staging a clear on every one of them.
      if (overrides[key] !== undefined) onOverride(key, null);
      return;
    }
    const value = Number(trimmed);
    if (!Number.isFinite(value)) return; // unreadable: leave as it was
    if (Math.abs(value - current) < 0.005) return; // untouched
    // Typing the projected figure is not an adjustment, so it doesn't become one.
    if (Math.abs(value - projected) < 0.005) onOverride(key, null);
    else onOverride(key, value);
  };

  const toggle = (key: string) =>
    setExpanded((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
    );

  const toggleWarning = (key: string) =>
    setWarnings((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
    );

  return (
    <>
      <div className="tosp-toolbar">
        <div className="tosp-section-title">Revenue Budget {year}</div>
      </div>

      {/* The table scrolls in its own pane rather than with the page, which is
          what lets the month header stay put above it — a header can only
          stick inside the thing that scrolls. */}
      <div className="tosp-grid-scroll tosp-budget-scroll">
        <div className="tosp-grid-body" style={{ minWidth: MIN_WIDTH }}>
          <div
            className="tosp-grid-header tosp-grid-header-sticky"
            style={{ gridTemplateColumns: GRID_COLUMNS }}
          >
            <div>Account</div>
            {MONTH_LABELS.map((label) => (
              <div key={label} className="tosp-num">
                {label}
              </div>
            ))}
            <div className="tosp-num">Total</div>
          </div>

          {lines.map((line, index) => {
            const isExpanded = expanded.includes(line.qboAccount);
            // The parent account, printed once above the first line under it —
            // the same grouping QuickBooks exports, so both halves of the
            // budget read alike.
            const startsGroup = index === 0 || lines[index - 1].group !== line.group;
            // Closed with its own subtotal, the way QuickBooks totals each
            // parent account — and the way the expense side reads.
            const endsGroup =
              index === lines.length - 1 || lines[index + 1].group !== line.group;
            const groupLines = endsGroup ? lines.filter((l) => l.group === line.group) : [];
            // Subtotals follow the figures shown, adjustments included — a
            // total that ignored an edit would contradict the column above it.
            const groupMonths = MONTH_LABELS.map((_, m) =>
              groupLines.reduce((sum, l) => sum + monthsOf(l)[m], 0),
            );
            const lineMonths = monthsOf(line);
            const lineTotal = lineMonths.reduce((a, b) => a + b, 0);
            return (
              <div key={line.qboAccount}>
                {startsGroup && (
                  <div className="tosp-grid-row tosp-budget-header-row">{line.group}</div>
                )}
                <div
                  className={`tosp-grid-row${index % 2 === 1 ? " tosp-row-alt" : ""}`}
                  style={{ gridTemplateColumns: GRID_COLUMNS }}
                >
                  <div>
                    <button
                      type="button"
                      className="tosp-accordion-toggle"
                      aria-expanded={isExpanded}
                      title={
                        line.contributions.length
                          ? isExpanded
                            ? "Hide detail"
                            : "Show detail"
                          : "Nothing to show"
                      }
                      onClick={() => toggle(line.qboAccount)}
                    >
                      <span className="tosp-accordion-caret">{isExpanded ? "▾" : "▸"}</span>
                      <span>{line.label}</span>
                    </button>
                    {/* Outside the expander, not inside it: a button cannot sit
                        within a button. Its own toggle, so the reason a line is
                        short can be read without opening the tenant breakdown —
                        and so a line that is merely missing one tenant says so
                        quietly rather than announcing itself as unprojected. */}
                    {line.gap && (
                      <button
                        type="button"
                        className="tosp-revenue-warn"
                        aria-expanded={warnings.includes(line.qboAccount)}
                        aria-label={`Why ${line.label} is incomplete`}
                        title="Why this line is incomplete"
                        onClick={() => toggleWarning(line.qboAccount)}
                      >
                        ⚠
                      </button>
                    )}
                    <div className="tosp-revenue-account">{line.qboAccount}</div>
                    {warnings.includes(line.qboAccount) && (
                      <div className="tosp-revenue-gap">{line.gap}</div>
                    )}
                  </div>
                  {/* A LINE'S MONTH IS A SUM AND IS NEVER TYPED INTO. Every
                      figure on this sheet belongs to a tenant, so that is where
                      it is entered; a line total that could be edited as well
                      would be a second, contradictory answer to the same
                      question, and the first thing to disagree with the rows
                      printed beneath it. */}
                  {lineMonths.map((amount, m) => (
                    <div key={m} className="tosp-num tosp-line-total">
                      {cellText(amount)}
                    </div>
                  ))}
                  <div className="tosp-num tosp-revenue-total">{money2(lineTotal)}</div>
                </div>

                {isExpanded && (
                  <div className="tosp-row-details">
                    {line.contributions.length === 0 ? (
                      <div>Nothing contributes to this line yet.</div>
                    ) : (
                      line.contributions.map((row) => {
                        // Only the recovery is read year on year: it is the
                        // one line whose rate a tenant asks about, and the one
                        // the published statements carry a rate for.
                        const rowTotal = row.months.reduce(
                          (total, _, m) => total + tenantAmount(line.key, row, m),
                          0,
                        );
                        const moved =
                          line.key === CAM_RECOVERY_KEY ? camChange(row.id, rowTotal) : null;
                        const movedLabel = moved
                          ? `${psf(moved.current)} per sq ft against ${psf(moved.previous)} in ${
                              year - 1
                            } — ${Math.abs(moved.percent).toFixed(1)}% ${moved.up ? "up" : "down"}`
                          : undefined;

                        return (
                        <div
                          key={row.id}
                          className="tosp-grid-row"
                          style={{ gridTemplateColumns: GRID_COLUMNS }}
                        >
                          {/* The tenant, and nothing else — a tenant with a
                              shop, a patio and a locker is one row for the
                              whole of what they owe. The two rates sit under
                              the name where there is a year to compare with:
                              the grid's columns are the twelve months, and a
                              rate is not a month. */}
                          <div className="tosp-cell-wrap">
                            {row.label}
                            {moved && (
                              <div className="tosp-revenue-account" title={movedLabel}>
                                {psf(moved.previous)} → {psf(moved.current)} /sq ft
                              </div>
                            )}
                          </div>
                          {/* Editable, and blank rather than $0.00 where there
                              is nothing: a tenant nobody could project — a new
                              business, or one that took a space over mid-year —
                              is a figure waiting to be entered, not a figure of
                              zero. Leaving it blank is a legitimate answer. */}
                          {row.months.map((_, m) => {
                            const key = tenantKey(line.key, row.id, m);
                            const amount = tenantAmount(line.key, row, m);
                            const edited = isEdited(key);
                            const active = editing?.key === key;
                            // Nothing KNOWN and nothing WORKED OUT are different
                            // statements. A tenant projected to stay under their
                            // breakpoint owes nil, and $0.00 says so; a tenant
                            // with no sales to project from owes an unknown, and
                            // that is the blank an admin has to fill.
                            const unresolved = !edited && !!row.unknown?.[m];
                            // Blank reads yellow whether the budget is frozen or
                            // not: a month nobody put a figure against is worth
                            // seeing either way.
                            //
                            // THE CAM RECOVERY IS NOT TYPED INTO, ever. Every
                            // other line here is projected from a lease or a
                            // rate, and an adjustment states what the admin
                            // expects instead. 41001 is different: it is the
                            // expense allocation read from the other side, so
                            // the place to change what a tenancy is recovered
                            // is the expense line it comes from. A figure typed
                            // here would be a second, contradictory answer that
                            // the next recalculation would silently disagree
                            // with.
                            if (locked || line.key === CAM_RECOVERY_KEY) {
                              return (
                                <div
                                  key={m}
                                  className={`tosp-num${
                                    unresolved || edited ? " tosp-assumed" : ""
                                  }`}
                                >
                                  {unresolved ? "—" : cellText(amount)}
                                </div>
                              );
                            }
                            return (
                              <div key={m} className="tosp-num">
                                <input
                                  className={`tosp-budget-cell${
                                    edited ? " tosp-assumed" : ""
                                  }${unresolved ? " tosp-blank" : ""}`}
                                  inputMode="decimal"
                                  placeholder="—"
                                  aria-label={`${line.label}, ${row.label}, ${MONTH_LABELS[m]}`}
                                  title={
                                    edited
                                      ? `Manually entered. Projected: ${
                                          row.months[m] === 0
                                            ? "nothing"
                                            : money2(row.months[m])
                                        }. Clear the cell to put it back.`
                                      : "Projected from this tenant's own record — type to adjust"
                                  }
                                  // Empty only where the figure is unknown, so
                                  // the dash placeholder shows. A month worked
                                  // out to nothing reads $0.00 like any other
                                  // figure.
                                  value={
                                    active ? editing.text : unresolved ? "" : cellText(amount)
                                  }
                                  onFocus={() =>
                                    setEditing({
                                      key,
                                      text:
                                        amount === 0
                                          ? ""
                                          : String(Math.round(amount * 100) / 100),
                                    })
                                  }
                                  onChange={(e) => setEditing({ key, text: e.target.value })}
                                  onBlur={(e) =>
                                    commit(key, e.target.value, row.months[m], amount)
                                  }
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter") e.currentTarget.blur();
                                    if (e.key === "Escape") setEditing(null);
                                  }}
                                />
                              </div>
                            );
                          })}
                          {/* The change sits immediately left of the year's
                              figure, in the same green and red the sales
                              sheets use for a month against last year's. */}
                          <div className="tosp-num tosp-revenue-change">
                            {moved && (
                              <span
                                className={`tosp-sales-arrow ${
                                  moved.up ? "tosp-sales-arrow-up" : "tosp-sales-arrow-down"
                                }`}
                                title={movedLabel}
                                aria-label={movedLabel}
                              >
                                {moved.up ? "▲" : "▼"} {Math.abs(moved.percent).toFixed(1)}%
                              </span>
                            )}
                            <span>{money2(rowTotal)}</span>
                          </div>
                        </div>
                        );
                      })
                    )}
                  </div>
                )}

                {endsGroup && (
                  <div
                    className="tosp-grid-row tosp-budget-total-row"
                    style={{ gridTemplateColumns: GRID_COLUMNS }}
                  >
                    <div>Total {line.group}</div>
                    {groupMonths.map((amount, m) => (
                      <div key={m} className="tosp-num">
                        {cellText(amount)}
                      </div>
                    ))}
                    <div className="tosp-num">
                      {money2(groupMonths.reduce((a, b) => a + b, 0))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}

          <div
            className="tosp-grid-row tosp-revenue-grand"
            style={{ gridTemplateColumns: GRID_COLUMNS }}
          >
            <div>Total Revenue</div>
            {/* Every total on the sheet goes through monthsOf, so a figure
                typed against one tenant reaches the line, its group and the
                grand total without three separate rules agreeing to. */}
            {MONTH_LABELS.map((_, m) => {
              const total = lines.reduce((sum, line) => sum + monthsOf(line)[m], 0);
              return (
                <div key={m} className="tosp-num">
                  {cellText(total)}
                </div>
              );
            })}
            <div className="tosp-num">
              {money2(
                lines.reduce(
                  (sum, line) => sum + monthsOf(line).reduce((t, amount) => t + amount, 0),
                  0,
                ),
              )}
            </div>
          </div>
        </div>
      </div>

      {/* NO BANNER ABOUT UNLET UNITS. A unit nobody held when the year opened
          earns nothing on the budget by design — that is the rule, not a fault
          — and announcing it on every budget said nothing an admin can act on.
          `unleasedUnits` is still on the projection for the diagnostic tools. */}

      {/* NO FOOTNOTE COUNTING THE PROJECTED LINES. Every line carries its own
          warning where it has something to say, which is where an admin can
          act on it; a tally at the foot of the sheet said nothing the lines
          above had not already said better. */}
    </>
  );
}
